import React, { useState, useEffect } from 'react';
import {
  FileText, Video, Presentation, Plus, Edit, Trash2, Eye,
  Link, ExternalLink, Play, BookOpen, Layers, X
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useCourse } from '../../../../../context/CourseContext';
import {
  addModuleContent,
  updateContentItem,
  deleteContentItem,
  getModuleById
} from '../../../../../services/content.service';
import { getCoursesById } from '../../../../../services/course.service';
import { getCardColor } from '../../../../../utils/courseHelpers';
import {
  card, cardInteractive, cardTitle, microLabel, bodySecondary,
  emptyState, dividerInner, iconSize, motionStaggerChild, motionHoverLift
} from '../../../../../lib/design-system';
import { cn } from '../../../../../lib/utils';

// ─── Content type config ─────────────────────────────────────────────────────

const CONTENT_TYPES = [
  { key: 'pdfs',   label: 'PDFs',          icon: FileText,     countKey: 'pdfCount' },
  { key: 'videos', label: 'Videos',        icon: Video,        countKey: 'videoCount' },
  { key: 'ppts',   label: 'Presentations', icon: Presentation, countKey: 'pptCount' },
  { key: 'links',  label: 'Links',         icon: Link,         countKey: 'linkCount' },
];

// ─── Main Component ──────────────────────────────────────────────────────────

const ContentSection = () => {
  const { courseData, setCourseData } = useCourse();
  const [selectedModule, setSelectedModule] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [selectedContentType, setSelectedContentType] = useState('pdfs');
  const [isLoading, setIsLoading] = useState(false);
  const [moduleLoading, setModuleLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const modules = courseData?.syllabus?.modules || [];

  useEffect(() => {
    if (modules.length > 0 && !selectedModule) {
      handleModuleSelect(modules[0], 0);
    }
  }, [courseData]);

  const handleModuleSelect = async (module, index) => {
    setSelectedIndex(index);
    setModuleLoading(true);
    try {
      const data = await getModuleById(courseData.id, module._id);
      setSelectedModule(data);
    } catch {
      setSelectedModule(module);
    } finally {
      setModuleLoading(false);
    }
  };

  const normalizeSyllabus = (responseCourse) => {
    if (!responseCourse?.syllabus) return { modules: [] };
    let mods = [];
    if (Array.isArray(responseCourse.syllabus)) {
      mods = responseCourse.syllabus;
    } else if (Array.isArray(responseCourse.syllabus.modules)) {
      mods = responseCourse.syllabus.modules;
    }
    mods = mods.map(m => ({ ...m, topics: Array.isArray(m.topics) ? m.topics : [] }));
    return { modules: mods };
  };

  const refreshCourseAndModule = async (moduleId) => {
    const freshCourse = await getCoursesById(courseData.id);
    const normalizedSyllabus = normalizeSyllabus(freshCourse);
    const newCourseData = { ...freshCourse, syllabus: normalizedSyllabus };
    setCourseData(newCourseData);
    // Re-fetch full module content
    try {
      const freshModule = await getModuleById(courseData.id, moduleId);
      setSelectedModule(freshModule);
    } catch {
      const fallback = newCourseData.syllabus.modules.find(m => m._id === moduleId);
      if (fallback) setSelectedModule(fallback);
    }
    setRefreshKey((k) => k + 1);
  };

  const handleAddContent = async (contentData, file, thumbnail) => {
    if (!selectedModule) return;
    setIsLoading(true);
    try {
      const formData = new FormData();
      formData.append('contentType', selectedContentType.slice(0, -1));
      formData.append('name', contentData.name);
      formData.append('description', contentData.description || '');
      if (selectedContentType === 'links' && contentData.url) {
        formData.append('url', contentData.url);
      }
      if (file) formData.append('file', file);
      if (thumbnail) formData.append('thumbnail', thumbnail);

      await addModuleContent(courseData.id, selectedModule._id, formData);
      await refreshCourseAndModule(selectedModule._id);
      setShowAddModal(false);
    } catch (error) {
      console.error('Error adding content:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteContent = async (contentId) => {
    if (!selectedModule || !window.confirm('Are you sure you want to delete this content?')) return;
    setIsLoading(true);
    try {
      const contentType = selectedContentType.slice(0, -1);
      await deleteContentItem(courseData.id, selectedModule._id, contentType, contentId);
      await refreshCourseAndModule(selectedModule._id);
    } catch (error) {
      console.error('Error deleting content:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateContent = async (updatedData, file, thumbnail) => {
    if (!selectedModule || !editingItem) return;
    setIsLoading(true);
    try {
      const formData = new FormData();
      formData.append('name', updatedData.name);
      formData.append('description', updatedData.description || '');
      if (selectedContentType === 'links' && updatedData.url) {
        formData.append('url', updatedData.url);
      }
      if (file) formData.append('file', file);
      if (thumbnail) formData.append('thumbnail', thumbnail);

      const contentType = selectedContentType.slice(0, -1);
      await updateContentItem(courseData.id, selectedModule._id, contentType, editingItem._id, formData);
      await refreshCourseAndModule(selectedModule._id);
      setEditingItem(null);
    } catch (error) {
      console.error('Error updating content:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return null;
    const k = 1024;
    const s = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + s[i];
  };

  const formatDate = (d) => {
    if (!d) return null;
    return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const activeColor = modules[selectedIndex] ? getCardColor(modules[selectedIndex].moduleTitle) : '#4f46e5';

  // ─── No modules empty state ────────────────────────────────────────────────
  if (!modules.length) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="mx-auto px-4 sm:px-6 py-6"
      >
        <div className={cn(emptyState, 'py-16')}>
          <Layers className={cn(iconSize.empty, 'mx-auto text-gray-300 dark:text-gray-600 mb-4')} />
          <p className="text-sm font-bold text-gray-500 dark:text-gray-400">No modules found</p>
          <p className={cn(bodySecondary, 'mt-1')}>Add modules in the Syllabus section</p>
        </div>
      </motion.div>
    );
  }

  // ─── Get items for current content type ────────────────────────────────────
  const currentItems = selectedModule?.[selectedContentType] || [];

  return (
    <div className="min-h-[600px]">
      <div className="mx-auto px-4 sm:px-6 py-6 space-y-6">

        {/* ── Module Navigation Strip ──────────────────────────────── */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className={cn(card, 'overflow-hidden')}
        >
          <div
            className="h-1.5 transition-colors duration-300"
            style={{ backgroundColor: activeColor }}
          />
          <div className="p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <Layers className={cn(iconSize.lg, 'text-gray-400 dark:text-gray-500')} />
                <h2 className={cardTitle}>Course Modules</h2>
              </div>
              <span className={cn(microLabel)}>
                {modules.length} modules
              </span>
            </div>

            {/* Chip grid */}
            <div className="flex flex-wrap gap-2">
              {modules.map((mod, i) => {
                const isActive = selectedIndex === i;
                const color = getCardColor(mod.moduleTitle);
                return (
                  <motion.button
                    key={mod._id}
                    whileHover={{ y: -1, scale: 1.05 }}
                    whileTap={{ scale: 0.97 }}
                    onClick={() => handleModuleSelect(mod, i)}
                    title={`Module ${mod.moduleNumber}: ${mod.moduleTitle}`}
                    className={cn(
                      'relative w-11 h-11 rounded-sm text-sm font-bold transition-all duration-200 flex items-center justify-center border',
                      isActive
                        ? 'text-white shadow-md border-transparent'
                        : 'bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                    )}
                    style={isActive ? {
                      backgroundColor: color,
                      boxShadow: `0 4px 12px ${color}40`,
                    } : {}}
                  >
                    {mod.moduleNumber}
                    {isActive && (
                      <motion.div
                        layoutId="teacherActiveChipDot"
                        className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-sm bg-white"
                        style={{ boxShadow: `0 0 4px ${color}` }}
                      />
                    )}
                  </motion.button>
                );
              })}
            </div>
          </div>
        </motion.div>

        {/* ── Selected Module Content ──────────────────────────────── */}
        <AnimatePresence mode="wait">
          {moduleLoading ? (
            <motion.div
              key="loader"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="space-y-6"
            >
              {/* Skeleton: Banner */}
              <div className={cn(card, 'overflow-hidden')}>
                <div className="h-24 sm:h-28 bg-gray-200 dark:bg-gray-700 animate-pulse" />
                <div className="p-4 sm:p-5 space-y-3">
                  <div className="h-3 w-48 bg-gray-200 dark:bg-gray-700 rounded-sm animate-pulse" />
                  <div className="flex gap-2">
                    {[1, 2, 3].map(i => (
                      <div key={i} className="h-6 w-20 bg-gray-100 dark:bg-gray-700/60 rounded-sm animate-pulse" />
                    ))}
                  </div>
                </div>
              </div>

              {/* Skeleton: Filter strip */}
              <div className="flex gap-2">
                {[1, 2, 3, 4].map(i => (
                  <div key={i} className="h-10 w-28 bg-gray-200 dark:bg-gray-700 rounded-sm animate-pulse" />
                ))}
              </div>

              {/* Skeleton: Section header */}
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-sm bg-gray-200 dark:bg-gray-700 animate-pulse" />
                <div className="h-4 w-24 bg-gray-200 dark:bg-gray-700 rounded-sm animate-pulse" />
                <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />
              </div>

              {/* Skeleton: Card grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {[1, 2, 3].map(i => (
                  <div key={i} className={cn(card, 'overflow-hidden')}>
                    <div className="h-32 sm:h-36 bg-gray-200 dark:bg-gray-700 animate-pulse" />
                    <div className="p-4 space-y-2.5">
                      <div className="h-4 w-3/4 bg-gray-200 dark:bg-gray-700 rounded-sm animate-pulse" />
                      <div className="h-3 w-full bg-gray-100 dark:bg-gray-700/60 rounded-sm animate-pulse" />
                      <div className="h-3 w-2/3 bg-gray-100 dark:bg-gray-700/60 rounded-sm animate-pulse" />
                      <div className="pt-2 flex gap-2">
                        <div className="h-8 w-8 bg-gray-200 dark:bg-gray-700 rounded-sm animate-pulse" />
                        <div className="h-8 w-8 bg-gray-200 dark:bg-gray-700 rounded-sm animate-pulse" />
                        <div className="h-8 w-8 bg-gray-200 dark:bg-gray-700 rounded-sm animate-pulse ml-auto" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          ) : selectedModule ? (
            <motion.div
              key={`${selectedModule._id}-${refreshKey}`}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.35 }}
              className="space-y-6"
            >
              {/* ── Module Header Banner ──────────────────────────────── */}
              <div className={cn(card, 'overflow-hidden')}>
                <div
                  className="relative h-24 sm:h-28 w-full overflow-hidden"
                  style={{
                    background: `linear-gradient(135deg, ${activeColor} 0%, ${activeColor} 60%, ${activeColor}cc 60%, ${activeColor}99 100%)`,
                  }}
                >
                  <div
                    className="absolute inset-0"
                    style={{
                      background: 'linear-gradient(135deg, transparent 50%, rgba(255,255,255,0.08) 50%)',
                    }}
                  />
                  <span className="absolute right-4 sm:right-6 top-1/2 -translate-y-1/2 text-6xl sm:text-7xl font-black text-white/15 select-none tracking-tighter">
                    {selectedModule.moduleNumber < 10 ? `0${selectedModule.moduleNumber}` : selectedModule.moduleNumber}
                  </span>
                  <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-5">
                    <p className="text-white/70 text-xs font-bold uppercase tracking-widest mb-1">
                      Module {selectedModule.moduleNumber}
                    </p>
                    <h2 className="text-lg sm:text-xl font-black tracking-tight text-white drop-shadow-sm">
                      {selectedModule.moduleTitle}
                    </h2>
                  </div>
                </div>

                <div className="p-4 sm:p-5">
                  {selectedModule.description && (
                    <p className={cn(bodySecondary, 'max-w-3xl mb-4')}>
                      {selectedModule.description}
                    </p>
                  )}
                  <div className="flex flex-wrap gap-2">
                    {CONTENT_TYPES.map(({ key, label, icon: Icon, countKey }) => {
                      const count = selectedModule[countKey] || selectedModule[key]?.length || 0;
                      if (count === 0) return null;
                      return (
                        <div
                          key={key}
                          className="flex items-center gap-1.5 px-2.5 py-1 rounded-sm bg-gray-100 dark:bg-gray-700/60 text-gray-600 dark:text-gray-300"
                        >
                          <Icon className={iconSize.xs} />
                          <span className="text-xs font-bold">{count}</span>
                          <span className="text-xs text-gray-400 dark:text-gray-500">{label}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* ── Content Type Filter Strip ─────────────────────────── */}
              <div className="flex flex-wrap items-center gap-2">
                {CONTENT_TYPES.map(({ key, label, icon: Icon, countKey }) => {
                  const count = selectedModule[countKey] || selectedModule[key]?.length || 0;
                  const isActive = selectedContentType === key;
                  return (
                    <button
                      key={key}
                      onClick={() => setSelectedContentType(key)}
                      className={cn(
                        'flex items-center gap-2 px-3 py-2 rounded-sm text-xs font-bold uppercase tracking-wider border transition-all duration-200',
                        isActive
                          ? 'bg-accent1/10 text-accent1 border-accent1'
                          : 'bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                      )}
                    >
                      <Icon className={iconSize.md} />
                      <span>{label}</span>
                      <span className={cn(
                        'px-1.5 py-0.5 rounded-sm text-xs font-bold',
                        isActive
                          ? 'bg-accent1/20 text-accent1'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500'
                      )}>
                        {count}
                      </span>
                    </button>
                  );
                })}

                {/* Add content button — right-aligned */}
                <button
                  onClick={() => setShowAddModal(true)}
                  className="ml-auto flex items-center gap-1.5 px-3 py-2 rounded-sm bg-accent1 text-white text-xs font-bold uppercase tracking-wider hover:opacity-90 transition-opacity"
                >
                  <Plus className={iconSize.md} />
                  <span>Add {CONTENT_TYPES.find(ct => ct.key === selectedContentType)?.label}</span>
                </button>
              </div>

              {/* ── Content Grid ──────────────────────────────────────── */}
              {currentItems.length === 0 ? (
                <div className={cn(emptyState, 'py-16')}>
                  <BookOpen className={cn(iconSize.empty, 'mx-auto text-gray-300 dark:text-gray-600 mb-4')} />
                  <p className="text-sm font-bold text-gray-500 dark:text-gray-400">
                    No {CONTENT_TYPES.find(ct => ct.key === selectedContentType)?.label.toLowerCase()} found
                  </p>
                  <p className={cn(bodySecondary, 'mt-1')}>
                    Click "Add {CONTENT_TYPES.find(ct => ct.key === selectedContentType)?.label}" to get started
                  </p>
                </div>
              ) : (
                <section>
                  {/* Section header */}
                  {(() => {
                    const ct = CONTENT_TYPES.find(c => c.key === selectedContentType);
                    const Icon = ct.icon;
                    return (
                      <div className="flex items-center gap-3 mb-4">
                        <div
                          className="w-8 h-8 rounded-sm flex items-center justify-center"
                          style={{ backgroundColor: `${activeColor}15` }}
                        >
                          <Icon className={iconSize.md} style={{ color: activeColor }} />
                        </div>
                        <h3 className={cardTitle}>{ct.label}</h3>
                        <span className="text-xs font-bold rounded-sm px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400">
                          {currentItems.length}
                        </span>
                        <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700 ml-2" />
                      </div>
                    );
                  })()}

                  {/* Cards grid */}
                  <div className={cn(
                    'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4',
                    isLoading && 'opacity-50 pointer-events-none'
                  )}>
                    {currentItems.map((item, idx) => (
                      <TeacherContentCard
                        key={item._id}
                        item={item}
                        contentType={selectedContentType}
                        accentColor={activeColor}
                        formatFileSize={formatFileSize}
                        formatDate={formatDate}
                        index={idx}
                        onEdit={() => setEditingItem(item)}
                        onDelete={() => handleDeleteContent(item._id)}
                      />
                    ))}
                  </div>
                </section>
              )}
            </motion.div>
          ) : null}
        </AnimatePresence>
      </div>

      {/* Add Content Modal */}
      {showAddModal && (
        <AddContentModal
          contentType={selectedContentType}
          onClose={() => setShowAddModal(false)}
          onAdd={handleAddContent}
          isLoading={isLoading}
        />
      )}

      {/* Edit Content Modal */}
      {editingItem && (
        <EditContentModal
          item={editingItem}
          contentType={selectedContentType}
          onClose={() => setEditingItem(null)}
          onUpdate={handleUpdateContent}
          isLoading={isLoading}
        />
      )}
    </div>
  );
};

// ─── Teacher Content Card ─────────────────────────────────────────────────────

const TeacherContentCard = ({
  item, contentType, accentColor, formatFileSize, formatDate, index,
  onEdit, onDelete
}) => {
  const TypeIcon = CONTENT_TYPES.find(ct => ct.key === contentType)?.icon || FileText;

  const handleView = () => {
    if (contentType === 'links') {
      window.open(item.url || item.link, '_blank');
    } else {
      window.open(item.fileUrl || item.videoUrl, '_blank');
    }
  };

  const size = formatFileSize(item.fileSize);
  const date = formatDate(item.createDate);

  return (
    <motion.div
      className={cn(cardInteractive, 'group flex flex-col')}
      {...motionStaggerChild(index)}
      whileHover={motionHoverLift}
    >
      {/* Thumbnail / colored header */}
      <div className="relative h-32 sm:h-36 bg-gray-100 dark:bg-gray-700 flex items-center justify-center overflow-hidden">
        {item.thumbnail?.thumbnailUrl ? (
          <>
            <img
              src={item.thumbnail.thumbnailUrl}
              alt={item.name || item.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-black/30 to-transparent" />
          </>
        ) : (
          <>
            <div
              className="absolute inset-0 opacity-[0.06] dark:opacity-[0.08]"
              style={{
                backgroundImage: `radial-gradient(circle at 20% 80%, ${accentColor} 0%, transparent 50%), radial-gradient(circle at 80% 20%, ${accentColor} 0%, transparent 50%)`,
              }}
            />
            <div className="relative flex flex-col items-center gap-2">
              <TypeIcon size={28} className="text-gray-300 dark:text-gray-500" />
            </div>
          </>
        )}

        {/* Video play overlay */}
        {contentType === 'videos' && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-10 h-10 rounded-sm flex items-center justify-center bg-white/90 dark:bg-gray-800/90 shadow-md group-hover:scale-105 transition-transform">
              <Play size={18} style={{ color: accentColor }} className="ml-0.5" />
            </div>
          </div>
        )}
      </div>

      {/* Body */}
      <div className="flex-1 flex flex-col p-4">
        <h3 className="font-bold text-sm text-gray-900 dark:text-white mb-1 line-clamp-2">
          {item.name || item.title}
        </h3>

        {(item.description || item.content) && (
          <p className={cn(bodySecondary, 'mb-2 line-clamp-2')}>
            {item.description || item.content}
          </p>
        )}

        {contentType === 'links' && (item.url || item.link) && (
          <p className="text-xs truncate mb-2" style={{ color: accentColor }} title={item.url || item.link}>
            {item.url || item.link}
          </p>
        )}

        <div className="flex-1" />

        {/* File info */}
        {(size || date) && (
          <div className={cn('flex items-center justify-between mb-3 pt-2 border-t', dividerInner)}>
            <span className={bodySecondary}>{size || ''}</span>
            <span className={bodySecondary}>{date || ''}</span>
          </div>
        )}

        {/* Teacher action buttons */}
        <div className={cn('flex items-center justify-between pt-2 border-t', dividerInner)}>
          <div className="flex items-center gap-1">
            <button
              onClick={handleView}
              className="flex flex-col items-center gap-0.5 p-2 rounded-sm text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors"
              title={contentType === 'links' ? 'Open Link' : 'View'}
            >
              {contentType === 'links' ? <ExternalLink size={16} /> : <Eye size={16} />}
              <span className="text-[10px] font-bold hidden sm:block">View</span>
            </button>
            <button
              onClick={onEdit}
              className="flex flex-col items-center gap-0.5 p-2 rounded-sm text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/30 transition-colors"
              title="Edit"
            >
              <Edit size={16} />
              <span className="text-[10px] font-bold hidden sm:block">Edit</span>
            </button>
          </div>
          <button
            onClick={onDelete}
            className="flex flex-col items-center gap-0.5 p-2 rounded-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors"
            title="Delete"
          >
            <Trash2 size={16} />
            <span className="text-[10px] font-bold hidden sm:block">Delete</span>
          </button>
        </div>
      </div>
    </motion.div>
  );
};

// ─── Modal input/label classes ────────────────────────────────────────────────

const modalInputClass =
  'w-full rounded-sm border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent1/30 focus:border-accent1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white';

const modalFileInputClass =
  'w-full rounded-sm border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent1/30 focus:border-accent1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white file:mr-4 file:py-1.5 file:px-3 file:rounded-sm file:border-0 file:text-xs file:font-bold file:uppercase file:tracking-wider file:bg-accent1/10 file:text-accent1 hover:file:bg-accent1/20';

// ─── Add Content Modal ───────────────────────────────────────────────────────

const AddContentModal = ({ contentType, onClose, onAdd, isLoading }) => {
  const [formData, setFormData] = useState({ name: '', description: '', url: '' });
  const [file, setFile] = useState(null);
  const [thumbnail, setThumbnail] = useState(null);

  const ctLabel = CONTENT_TYPES.find(ct => ct.key === contentType)?.label || '';

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name.trim()) return;
    if (contentType === 'links' && !formData.url.trim()) return;
    onAdd(formData, file, thumbnail);
  };

  const getAcceptedFileTypes = () => {
    switch (contentType) {
      case 'pdfs': return '.pdf';
      case 'ppts': return '.ppt,.pptx';
      case 'videos': return '.mp4,.avi,.mov,.wmv,.flv,.webm';
      default: return '';
    }
  };

  const isFileRequired = () => contentType !== 'links';

  return (
    <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50 p-4">
      <div className={cn(card, 'max-w-md w-full max-h-[90vh] overflow-hidden flex flex-col')}>
        {/* Header */}
        <div className={cn('flex items-center justify-between p-4 sm:p-5 border-b', dividerInner)}>
          <h2 className={cardTitle}>Add {ctLabel}</h2>
          <button
            onClick={onClose}
            className="p-1.5 rounded-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <X className={iconSize.md} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-4 sm:p-5 space-y-4">
          <div>
            <label className={cn(microLabel, 'mb-1.5 block')}>Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className={modalInputClass}
              required
            />
          </div>

          <div>
            <label className={cn(microLabel, 'mb-1.5 block')}>Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              className={modalInputClass}
            />
          </div>

          {contentType === 'links' && (
            <div>
              <label className={cn(microLabel, 'mb-1.5 block')}>URL *</label>
              <input
                type="url"
                value={formData.url}
                onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                className={modalInputClass}
                placeholder="https://example.com"
                required
              />
            </div>
          )}

          {isFileRequired() && (
            <div>
              <label className={cn(microLabel, 'mb-1.5 block')}>File *</label>
              <input
                type="file"
                accept={getAcceptedFileTypes()}
                onChange={(e) => setFile(e.target.files[0])}
                className={modalFileInputClass}
                required
              />
            </div>
          )}

          <div>
            <label className={cn(microLabel, 'mb-1.5 block')}>Thumbnail</label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => setThumbnail(e.target.files[0])}
              className={modalFileInputClass}
            />
          </div>

          {/* Footer buttons */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 rounded-sm border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors text-xs font-bold uppercase tracking-wider"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="flex-1 px-4 py-2 rounded-sm bg-accent1 text-white hover:opacity-90 transition-opacity disabled:opacity-50 text-xs font-bold uppercase tracking-wider"
            >
              {isLoading ? 'Adding...' : 'Add'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Edit Content Modal ──────────────────────────────────────────────────────

const EditContentModal = ({ item, contentType, onClose, onUpdate, isLoading }) => {
  const [formData, setFormData] = useState({
    name: item.name || item.title || '',
    description: item.description || item.content || '',
    url: item.url || item.link || ''
  });
  const [file, setFile] = useState(null);
  const [thumbnail, setThumbnail] = useState(null);

  const ctLabel = CONTENT_TYPES.find(ct => ct.key === contentType)?.label || '';

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name.trim()) return;
    if (contentType === 'links' && !formData.url.trim()) return;
    onUpdate(formData, file, thumbnail);
  };

  const getAcceptedFileTypes = () => {
    switch (contentType) {
      case 'pdfs': return '.pdf';
      case 'ppts': return '.ppt,.pptx';
      case 'videos': return '.mp4,.avi,.mov,.wmv,.flv,.webm';
      default: return '';
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50 p-4">
      <div className={cn(card, 'max-w-md w-full max-h-[90vh] overflow-hidden flex flex-col')}>
        {/* Header */}
        <div className={cn('flex items-center justify-between p-4 sm:p-5 border-b', dividerInner)}>
          <h2 className={cardTitle}>Edit {ctLabel}</h2>
          <button
            onClick={onClose}
            className="p-1.5 rounded-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <X className={iconSize.md} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-4 sm:p-5 space-y-4">
          <div>
            <label className={cn(microLabel, 'mb-1.5 block')}>Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className={modalInputClass}
              required
            />
          </div>

          <div>
            <label className={cn(microLabel, 'mb-1.5 block')}>Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              className={modalInputClass}
            />
          </div>

          {contentType === 'links' && (
            <div>
              <label className={cn(microLabel, 'mb-1.5 block')}>URL *</label>
              <input
                type="url"
                value={formData.url}
                onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                className={modalInputClass}
                placeholder="https://example.com"
                required
              />
            </div>
          )}

          {contentType !== 'links' && (
            <div>
              <label className={cn(microLabel, 'mb-1.5 block')}>Replace File (optional)</label>
              <input
                type="file"
                accept={getAcceptedFileTypes()}
                onChange={(e) => setFile(e.target.files[0])}
                className={modalFileInputClass}
              />
            </div>
          )}

          <div>
            <label className={cn(microLabel, 'mb-1.5 block')}>Replace Thumbnail (optional)</label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => setThumbnail(e.target.files[0])}
              className={modalFileInputClass}
            />
          </div>

          {/* Footer buttons */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 rounded-sm border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors text-xs font-bold uppercase tracking-wider"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="flex-1 px-4 py-2 rounded-sm bg-accent1 text-white hover:opacity-90 transition-opacity disabled:opacity-50 text-xs font-bold uppercase tracking-wider"
            >
              {isLoading ? 'Updating...' : 'Update'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ContentSection;
